
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { useAuthGuard } from "@/hooks/useAuthGuard";

/**
 * Hook for managing resume optimization process
 */
export const useOptimizationProcess = (
  onTabChange: (tab: string) => void,
  resetJobDetails: () => void
) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { handleApiError } = useAuthGuard();

  // Process the resume optimization
  const processOptimization = async (
    resumeContent: string,
    jobTitle: string,
    company: string,
    jobDescription: string,
    selectedResumeId: string | null,
    originalFilename: string
  ) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You need to be logged in to optimize your resume",
        variant: "destructive",
      });
      return false;
    }
    
    setIsProcessing(true);
    setApiError(null);
    try {
      if (!resumeContent || !jobDescription || !jobTitle || !company) {
        throw new Error("Please fill in all required fields");
      }
      
      console.log("Calling optimize-resume edge function...");
      
      try {
        const { data, error } = await supabase.functions.invoke('optimize-resume', {
          body: {
            resumeText: resumeContent,
            jobDescription,
            jobTitle,
            company,
          }
        });

        if (error) {
          console.error("Edge function error:", error);
          if (error.message?.includes('429')) {
            throw new Error("The AI service has reached its request limit. Please try again later.");
          } else if (error.message?.includes('fetch') || error.message?.includes('Failed to send')) {
            throw new Error("Connection issue: The optimization service is currently unavailable. This is expected in development environments where Edge Functions aren't fully configured. In production, this would connect to the actual AI service.");
          } else {
            throw error;
          }
        }
        
        if (!data || !data.optimizedResume) {
          throw new Error("Failed to optimize resume. Please try again.");
        }

        // Get the parent resume id - either the selected one or the newly uploaded one
        let parentResumeId = selectedResumeId;
        
        // If we don't have a selected resume ID but have uploaded a new resume
        if (!selectedResumeId) {
          // First check if we already have an original resume with this filename
          const { data: existingResume } = await supabase
            .from('resumes')
            .select('id')
            .eq('user_id', user.id)
            .eq('original_filename', originalFilename)
            .eq('is_original', true)
            .maybeSingle();
            
          if (existingResume?.id) {
            parentResumeId = existingResume.id;
          } else {
            // Save the new original resume
            const { data: newOriginalResume, error: originalError } = await supabase
              .from('resumes')
              .insert({
                user_id: user.id,
                content: resumeContent,
                title: `Original Resume - ${originalFilename}`,
                is_original: true,
                original_filename: originalFilename,
                status: 'completed'
              })
              .select()
              .single();

            if (originalError) throw originalError;
            parentResumeId = newOriginalResume.id;
          }
        }

        // Insert optimized version linked to original
        const { error: insertError } = await supabase
          .from('resumes')
          .insert({
            title: `${jobTitle} at ${company}`,
            content: data.optimizedResume,
            preview_content: data.previewContent,
            download_content: data.optimizedResume,
            user_id: user.id,
            is_paid: false,
            is_original: false,
            parent_resume_id: parentResumeId
          });

        if (insertError) throw insertError;

        toast({
          title: "Success",
          description: "Your resume has been optimized! Check the Saved Resumes tab to preview it. You can now optimize the same resume for another job.",
        });
        
        // Only reset job-related fields
        resetJobDetails();
        onTabChange('saved');
        return true;
        
      } catch (functionError: any) {
        // Handle edge function specific errors
        console.error("Edge function specific error:", functionError);
        
        // Provide development mode message for connection errors
        if (functionError.message?.includes('fetch') || functionError.message?.includes('Failed to send')) {
          setApiError("Development Mode: Edge Function connection failed. This is expected in local development. In production, your resume would be processed by our AI service.");
          
          toast({
            title: "Development Mode",
            description: "Edge Functions aren't available in this environment. In production, your resume would be processed by our AI service.",
            variant: "default",
          });
        } else {
          setApiError(functionError.message || "An unexpected error occurred with the optimization service.");
          
          toast({
            title: "Optimization Error",
            description: functionError.message || "An unexpected error occurred with the optimization service.",
            variant: "destructive",
          });
        }
        
        return false;
      }
      
    } catch (error: any) {
      console.error("Optimization error:", error);
      setApiError(error.message || "An unexpected error occurred. Please try again later.");
      toast({
        title: "Optimization Failed",
        description: error.message || "An unexpected error occurred. Please try again later.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    isProcessing,
    apiError,
    processOptimization
  };
};
